import { RegisteredStarComponent } from '../../components/registered-star/registered-star.component';
import { countPodiums } from '../../models/podium.models';
import { withCategoryStanding } from '../../models/podium.models';
import { Component, ChangeDetectionStrategy, inject, signal, computed, input, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DogService } from '../../services/dog.service';
import { HumanService } from '../../services/human.service';
import { ResultService } from '../../services/result.service';
import { TranslationService } from '../../services/translation.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { DisplayValuePipe } from '../../pipes/display-value.pipe';
import { DistancePipe } from '../../pipes/distance.pipe';
import { ResultStatusPipe } from '../../pipes/result-status.pipe';
import { DisciplinePipe } from '../../pipes/discipline.pipe';
import { BreedNamePipe } from '../../pipes/breed-name.pipe';
import { RouterModule } from '@angular/router';
import { BackNavigationDirective } from '../../directives/back-navigation.directive';
import { Dog, Human, Result, Race, Event } from '../../models/data.models';
import { BrandedLoaderComponent } from '../../components/branded-loader/branded-loader.component';
import { collapseKnownDogDuplicates, isKnownLaikoAlias } from '../../models/dog-identity.models';

@Component({
  selector: 'app-dog-profile',
  standalone: true,
  imports: [RegisteredStarComponent, CommonModule, TranslatePipe, DisplayValuePipe, DistancePipe, ResultStatusPipe, DisciplinePipe, BreedNamePipe, RouterModule, BackNavigationDirective, BrandedLoaderComponent],
  templateUrl: './dog-profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DogProfileComponent {
  dogService = inject(DogService);
  humanService = inject(HumanService);
  resultService = inject(ResultService);
  translationService = inject(TranslationService);

  dogId = input.required<string>({ alias: 'id' });

  dog = signal<Dog | null>(null);
  owner = signal<Human | null>(null);
  dogResults = signal<(Result & { race: Race; event?: Event; rank?: number })[]>([]);
  participantCounts = signal<Record<string, number>>({});
  categoryParticipantCounts = signal<Record<string, number>>({});
  isLoading = signal(true);
  readonly defaultDogImage = '/assets/dog-default-profile.webp';
  private profileLoadVersion = 0;

  getFlagEmoji = this.humanService.getFlagEmoji;

  constructor() {
    effect(() => {
      this.loadProfile(this.dogId());
    });
  }

  async loadProfile(id: string) {
    const version = ++this.profileLoadVersion;
    this.isLoading.set(true);
    this.owner.set(null);
    this.dogResults.set([]);
    this.participantCounts.set({});
    this.categoryParticipantCounts.set({});
    try {
      const dog = await this.dogService.getDog(id);
      if (version !== this.profileLoadVersion) return;
      this.dog.set(dog);

      if (dog) {
        const [owner, results] = await Promise.all([
          dog.ownerId ? this.humanService.getHuman(dog.ownerId) : Promise.resolve(null),
          dog.ownerId
            ? this.resultService.getResultsByHuman(dog.ownerId).then(results => {
                const collapsed = collapseKnownDogDuplicates(results);
                const canonicalId = collapsed.find(result => result.team?.dog?.id === id)?.team?.dog?.id
                  ?? collapsed.find(result => result.team?.dog && result.team.dog.name === 'Laïko' && isKnownLaikoAlias(dog.name))?.team?.dog?.id
                  ?? id;
                return collapsed.filter(result => result.team?.dog?.id === canonicalId);
              })
            : this.resultService.getResultsByDog(id),
        ]);
        if (version !== this.profileLoadVersion) return;
        this.owner.set(owner);
        const sortedResults = results.sort((a, b) => new Date(b.event?.date || 0).getTime() - new Date(a.event?.date || 0).getTime());
        
        const raceIds = [...new Set(results.map(r => r.raceId))];
        const [counts, categoryStandings] = await Promise.all([
          this.resultService.getRaceParticipantCounts(raceIds),
          this.resultService.getCategoryStandings(raceIds),
        ]);
        if (version !== this.profileLoadVersion) return;

        const mappedResults = sortedResults.map(r => {
          const categoryRank = withCategoryStanding(r, categoryStandings[r.id]).categoryRank;
          return { ...r, categoryRank, rank: categoryRank };
        });

        this.dogResults.set(mappedResults);
        this.participantCounts.set(counts);
        this.categoryParticipantCounts.set(
          Object.fromEntries(
            results.map(result => [result.id, categoryStandings[result.id]?.participantCount ?? 0]),
          ),
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (version === this.profileLoadVersion) this.isLoading.set(false);
    }
  }

  dogStats = computed(() => {
    const results = this.dogResults();
    if (results.length === 0) {
      return {
        numberOfRaces: 0,
        totalDistanceCovered: 0,
        averageSpeed: 0,
      };
    }

    const stats = results.reduce(
      (acc, result) => {
        if (result.race && result.timeSeconds > 0) {
          const distance = result.race.distanceKm;
          acc.totalDistanceCovered += distance;

          const speed = result.averageSpeedKmph ?? (distance / (result.timeSeconds / 3600));
          acc.totalSpeed += speed;
        }
        return acc;
      },
      {
        totalDistanceCovered: 0,
        totalSpeed: 0,
      }
    );

    const timedRacesCount = results.filter(r => r.timeSeconds > 0).length;

    return {
      numberOfRaces: results.length,
      totalDistanceCovered: stats.totalDistanceCovered,
      averageSpeed: timedRacesCount > 0 ? stats.totalSpeed / timedRacesCount : 0,
    };
  });

  distanceBreakdown = computed(() => {
    const results = this.dogResults();
    const breakdown = {
      vtt: 0,
      trott: 0,
      run: 0,
      walk: 0
    };
    results.forEach(r => {
      const d = r.race.distanceKm || 0;
      const disc = r.race.discipline || '';
      if (disc === 'Cani-VTT') breakdown.vtt += d;
      else if (disc === 'Cani-Trottinette') breakdown.trott += d;
      else if (disc === 'Cani-Marche') breakdown.walk += d;
      else breakdown.run += d;
    });
    return [
      { label: '🚲', value: breakdown.vtt.toFixed(1) },
      { label: '🛴', value: breakdown.trott.toFixed(1) },
      { label: '🏃', value: breakdown.run.toFixed(1) },
      { label: '🥾', value: breakdown.walk.toFixed(1) }
    ].filter(item => parseFloat(item.value) > 0);
  });

  podiums = computed(() => {
    const results = this.dogResults();
    return countPodiums(results);
  });

  hasPodiums = computed(() => {
    const p = this.podiums();
    return p.first > 0 || p.second > 0 || p.third > 0 || p.fourth > 0;
  });

  groupedResults = computed(() => {
    const results = this.dogResults();
    const counts = this.participantCounts();
    const groups: { event: Event; results: any[] }[] = [];

    results.forEach(res => {
      const event = res.event || { id: 'unknown', name: 'Autre', date: '', location: '' } as Event;
      let group = groups.find(g => g.event.id === event.id);
      if (!group) {
        group = { event, results: [] };
        groups.push(group);
      }
      group.results.push({
        ...res,
        totalParticipants: counts[res.raceId] || 0
      });
    });

    return groups;
  });

  formatTime(totalSeconds: number): string {
    if (!totalSeconds || totalSeconds <= 0) return '—';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const pad = (num: number) => num.toString().padStart(2, '0');
    if (hours > 0) {
      return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
    }
    return `${pad(minutes)}m ${pad(seconds)}s`;
  }

  formatDate(isoString: string): string {
    const locale = this.translationService.currentLang() === 'fr' ? 'fr-FR' : 'en-US';
    return new Date(isoString).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  formatSpeed(speed: number): string {
    const locale = this.translationService.currentLang() === 'fr' ? 'fr-FR' : 'en-US';
    return speed.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
